import { useCallback, useEffect, useRef, useState } from "react";

// How many pixels from the bottom of the container to enable auto-scroll
const ACTIVATION_THRESHOLD = 50;

export function useAutoScroll(
  dependencies: React.DependencyList,
  chatId?: string | null,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousScrollTop = useRef<number | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  const forceScrollToBottom = useCallback(() => {
    setShouldAutoScroll(true);
    // Use requestAnimationFrame to ensure DOM is updated before scrolling
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    });
  }, []);

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      const isScrollingUp = previousScrollTop.current
        ? scrollTop < previousScrollTop.current
        : false;

      const isAtBottom =
        Math.abs(scrollHeight - scrollTop - clientHeight) <
        ACTIVATION_THRESHOLD;

      setIsScrolledToBottom(isAtBottom);

      // Only disable auto-scroll if user is actively scrolling up
      // Don't disable it if we're just detecting scroll position changes due to content updates
      if (isScrollingUp) {
        setShouldAutoScroll(false);
      } else if (isAtBottom) {
        setShouldAutoScroll(true);
      }

      previousScrollTop.current = scrollTop;
    }
  }, []);

  const handleTouchStart = useCallback(() => {
    setShouldAutoScroll(false);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      previousScrollTop.current = containerRef.current.scrollTop;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need chatId changing to trigger another check
  useEffect(() => {
    if (shouldAutoScroll) {
      // Use requestAnimationFrame to ensure DOM updates before scrolling
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [...dependencies, chatId]);

  return {
    containerRef,
    scrollToBottom,
    forceScrollToBottom,
    handleScroll,
    shouldAutoScroll,
    handleTouchStart,
    isScrolledToBottom,
  };
}
